import { arrowTopRightWhite } from '../../assets/hostedassets';
import HeaderBodyBox from '../HeaderBodyBox/HeaderBodyBox';
import {
    ALL_KNOWN_DETAILS_TESTIDS,
    ALL_KNOWN_DETAILS_TITLE,
    EMPTY_VALUE_PLACEHOLDER,
    KNOWN_DETAIL_CARD_ORDER,
    KNOWN_DETAIL_NOUN,
    KNOWN_DETAIL_ROW_ICON,
} from './constants';
import {
    attributeValues,
    deriveTotalCount,
    displayValueFor,
    groupsForType,
    maskedHintFor,
    orgEntity,
    primaryValueOf,
} from './utils/allKnownDetailsUtils';
import AllKnownDetailsDrawer from './AllKnownDetailsDrawer';
import MoreValuesTrigger from './components/MoreValuesTrigger/MoreValuesTrigger';
import SourceAttribution from './components/SourceAttribution/SourceAttribution';
import { useAllKnownDetails } from './hooks/useAllKnownDetails';
import { useAllKnownDetailsDrawer } from './hooks/useAllKnownDetailsDrawer';
import type { AllKnownDetailsData } from './types';
import styles from './AllKnownDetailsCard.module.scss';

export const AllKnownDetails = ({ data }: { data: AllKnownDetailsData | null }) => {
    const { isOpen, activeType, openDrawer, closeDrawer, setActiveType } = useAllKnownDetailsDrawer();

    const totalCount = deriveTotalCount(data);
    // Hide only when nothing is known for the company or any director.
    if (totalCount === 0) return null;

    const org = orgEntity(data);

    return (
        <>
            <HeaderBodyBox
                title={ALL_KNOWN_DETAILS_TITLE}
                className={styles.Card}
                headerClassName={styles.CardHeader}
                bodyClassName={styles.CardBody}
                headerRight={
                    <button
                        type="button"
                        className={styles.ViewAll}
                        data-testid={ALL_KNOWN_DETAILS_TESTIDS.VIEW_ALL}
                        onClick={() => openDrawer()}
                    >
                        <span>{`View All (${totalCount})`}</span>
                        <span className={styles.ViewAllArrow}>
                            <img src={arrowTopRightWhite} alt="" />
                        </span>
                    </button>
                }
            >
                <div className={styles.Rows} data-testid={ALL_KNOWN_DETAILS_TESTIDS.CARD}>
                    {KNOWN_DETAIL_CARD_ORDER.map((type) => {
                        const attributed = org ? attributeValues(groupsForType(org, type), org.entityType) : [];
                        const primary = primaryValueOf(attributed);

                        return (
                            <div className={styles.Row} key={type} data-testid={ALL_KNOWN_DETAILS_TESTIDS.CARD_ROW}>
                                <img src={KNOWN_DETAIL_ROW_ICON[type]} alt={KNOWN_DETAIL_NOUN[type].singular} className={styles.RowIcon} />
                                <div className={styles.RowValue}>
                                    <span title={maskedHintFor(primary?.value, type)}>
                                        {primary ? displayValueFor(primary.value, type) : EMPTY_VALUE_PLACEHOLDER}
                                    </span>
                                    <SourceAttribution source={primary?.source} />
                                    <MoreValuesTrigger count={attributed.length - 1} onActivate={() => openDrawer(type)} />
                                </div>
                            </div>
                        );
                    })}
                </div>
            </HeaderBodyBox>
            {isOpen && <AllKnownDetailsDrawer data={data} activeType={activeType} onTypeChange={setActiveType} onClose={closeDrawer} />}
        </>
    );
};

const AllKnownDetailsCard = ({ orgId }: { orgId?: string }) => {
    const data = useAllKnownDetails(orgId);
    return <AllKnownDetails data={data} />;
};

export default AllKnownDetailsCard;
